import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";

import { ProductShell, UpgradeNotice } from "@/components/app/product-shell";
import { useEntitlements } from "@/lib/billing/use-entitlements";
import { CsvLeadImport } from "@/components/app/csv-lead-import";
import { ErrorBoundary } from "@/components/error-boundary";
import {
  STAGES,
  STAGE_LABEL,
  deleteDealRecord,
  listDealRecords,
  setDealStage,
  upsertDealRecord,
  type DealRecord,
  type Stage,
  type Temperature,
} from "@/lib/pipeline/pipeline.functions";

export const Route = createFileRoute("/_authenticated/app/pipeline")({
  head: () => ({
    meta: [
      { title: "Deal pipeline — AROS FLOW" },
      {
        name: "description",
        content:
          "Every deal with a stage, a temperature, a next action and a date, saved to your account so nothing goes cold.",
      },
      { property: "og:title", content: "Deal pipeline — AROS FLOW" },
      {
        property: "og:description",
        content: "Track deals from new through contract with stages, temperature and next actions.",
      },
      { property: "og:type", content: "website" },
    ],
  }),
  component: PipelinePage,
});

function PipelinePage() {
  return (
    <ProductShell
      title="Pipeline"
      subtitle="Ten stages from new lead to closed or dead. Temperature is tracked separately from stage."
    >
      <ErrorBoundary label="Pipeline crashed">
        <PipelineGate />
      </ErrorBoundary>
    </ProductShell>
  );
}

function PipelineGate() {
  const ent = useEntitlements();
  if (!ent.loading && !ent.inPlan("pipeline")) {
    return <UpgradeNotice feature="Deal pipeline, tasks and offer history" plan="Pro" />;
  }
  return (
    <>
      <div className="mb-5">
        <ErrorBoundary label="Lead import crashed">
          <CsvLeadImport />
        </ErrorBoundary>
      </div>
      <PipelineBody />
    </>
  );
}

const TEMPS: Temperature[] = ["hot", "warm", "cold"];

function PipelineBody() {
  const qc = useQueryClient();
  const fetchDeals = useServerFn(listDealRecords);
  const saveDeal = useServerFn(upsertDealRecord);
  const moveDeal = useServerFn(setDealStage);
  const removeDeal = useServerFn(deleteDealRecord);

  const { data: deals = [], isLoading } = useQuery({
    queryKey: ["deal-records"],
    queryFn: () => fetchDeals(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["deal-records"] });

  const create = useMutation({
    mutationFn: (input: { label: string; addressText: string }) => saveDeal({ data: input }),
    onSuccess: () => {
      toast.success("Deal added");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const move = useMutation({
    mutationFn: (input: { id: string; stage: Stage }) => moveDeal({ data: input }),
    onSuccess: invalidate,
    onError: (e: Error) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: (id: string) => removeDeal({ data: { id } }),
    onSuccess: () => {
      toast.success("Deal removed");
      invalidate();
    },
  });

  const [label, setLabel] = useState("");
  const [address, setAddress] = useState("");

  return (
    <div className="space-y-5">
      <form
        className="flex flex-col gap-2 rounded-xl border border-[var(--glass-border)] bg-card p-4 sm:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          if (!label.trim()) return;
          create.mutate({ label: label.trim(), addressText: address.trim() });
          setLabel("");
          setAddress("");
        }}
      >
        <div className="flex-1">
          <label htmlFor="deal-label" className="text-[11px] text-muted-foreground">
            Deal name
          </label>
          <input
            id="deal-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Seller name or nickname"
            className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="deal-address" className="text-[11px] text-muted-foreground">
            Address
          </label>
          <input
            id="deal-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="123 Main St, City, ST"
            className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={create.isPending}
          className="focus-key mt-auto rounded-lg gradient-gold px-4 py-2 text-sm font-semibold text-black disabled:opacity-60"
        >
          Add deal
        </button>
      </form>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading your pipeline…</p>
      ) : deals.length === 0 ? (
        <p className="rounded-xl border border-[var(--glass-border)] bg-card p-6 text-sm text-muted-foreground">
          No deals yet. Add one above, or analyze a property and save it here.
        </p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {STAGES.filter((s) => deals.some((d: DealRecord) => d.stage === s)).map((stage) => (
            <section
              key={stage}
              className="rounded-xl border border-[var(--glass-border)] bg-card p-3"
            >
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gold">
                {STAGE_LABEL[stage]}{" "}
                <span className="text-muted-foreground">
                  ({deals.filter((d: DealRecord) => d.stage === stage).length})
                </span>
              </h2>
              <ul className="mt-2 space-y-2">
                {deals
                  .filter((d: DealRecord) => d.stage === stage)
                  .map((d: DealRecord) => (
                    <li
                      key={d.id}
                      className="rounded-lg border border-[var(--glass-border)] p-3 text-xs"
                    >
                      <div className="font-semibold">{d.label}</div>
                      {d.addressText && (
                        <div className="text-[11px] text-muted-foreground">{d.addressText}</div>
                      )}
                      <div className="mt-1 flex flex-wrap gap-2 text-[10px] text-muted-foreground">
                        {d.temperature && <span className="uppercase">{d.temperature}</span>}
                        {d.needScore != null && <span>Need {d.needScore}</span>}
                        {d.nextAction && <span>Next: {d.nextAction}</span>}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <label className="sr-only" htmlFor={`stage-${d.id}`}>
                          Stage for {d.label}
                        </label>
                        <select
                          id={`stage-${d.id}`}
                          value={d.stage}
                          onChange={(e) =>
                            move.mutate({ id: d.id, stage: e.target.value as Stage })
                          }
                          className="focus-key rounded-md border border-[var(--glass-border)] bg-input px-2 py-1 text-[11px]"
                        >
                          {STAGES.map((s) => (
                            <option key={s} value={s}>
                              {STAGE_LABEL[s]}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => del.mutate(d.id)}
                          className="focus-key rounded-md border border-white/15 px-2 py-1 text-[11px] text-white/70 hover:bg-white/5"
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">
        Temperature ({TEMPS.join(", ")}) is separate from stage on purpose — a contract can be cold
        and a new lead can be hot.
      </p>
    </div>
  );
}
